// iOS Safari compatibility utilities for push notifications

// Detect iOS devices (including iPadOS)
export function isIOSDevice() {
  const userAgent = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isiPadOS = /Macintosh/.test(userAgent) && 'ontouchend' in document;
  return isIOS || isiPadOS;
}

// Check if app is installed to home screen on iOS
export function isIOSInstalled() {
  if (!isIOSDevice()) return false;
  
  // Check for standalone mode (installed to home screen)
  return window.navigator.standalone === true;
}

// Check if iOS Safari supports push notifications
export function isIOSPushSupported() {
  if (!isIOSDevice()) return true; // Not iOS, assume supported
  
  // iOS Safari only supports push when installed to home screen
  return isIOSInstalled() && 'PushManager' in window && 'serviceWorker' in navigator;
}

// Get iOS installation prompt message
export function getIOSInstallMessage() {
  const userAgent = navigator.userAgent;
  const isiPad = /iPad/.test(userAgent) || (/Macintosh/.test(userAgent) && 'ontouchend' in document);
  
  if (isiPad) {
    return {
      title: "Install to enable notifications",
      steps: [
        "Tap the Share button",
        "Scroll down and tap 'Add to Home Screen'", 
        "Tap 'Add' to install the app",
        "Open the app from your home screen"
      ]
    };
  } else {
    return {
      title: "Install to enable notifications", 
      steps: [
        "Tap the Share button", 
        "Tap 'Add to Home Screen'",
        "Tap 'Add' to install the app",
        "Open the app from your home screen"
      ]
    };
  }
}

// Enhanced service worker registration for iOS
export async function registerServiceWorkerForIOS() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers not supported');
  }

  try {
    // Register service worker
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none' // Important for iOS to get updates
    });

    console.log('📱 Service worker registered for iOS:', registration.scope);

    // Wait for service worker to be ready
    await navigator.serviceWorker.ready;
    
    // iOS-specific: Force update check
    if (isIOSDevice()) {
      registration.addEventListener('updatefound', () => {
        console.log('📱 iOS: Service worker update found');
        const newWorker = registration.installing;
        
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed') {
            console.log('📱 iOS: New service worker installed');
            // On iOS, immediately activate new service worker
            if (navigator.serviceWorker.controller) {
              newWorker.postMessage({ action: 'skipWaiting' });
            }
          }
        });
      });
      
      // Check for updates more frequently on iOS
      setInterval(() => {
        registration.update();
      }, 60000); // Check every minute
    }

    return registration;
    
  } catch (error) {
    console.error('Failed to register service worker for iOS:', error);
    throw error;
  }
}

// iOS-specific push subscription options
export function getIOSPushOptions() {
  return {
    userVisibleOnly: true,
    applicationServerKey: 'BBEi59kjTeTrYzSu_G7NuxYRloupgbxrRA2E_g124r5gvJZBmu_VYTbwWXmsVKEU2_bbWEx71TTL_9n3h_plrjA'
  };
}

// Check iOS version for push notification support
export function getIOSVersion() {
  if (!isIOSDevice()) return null;
  
  const match = navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
  if (match) {
    return {
      major: parseInt(match[1], 10),
      minor: parseInt(match[2], 10),
      patch: parseInt(match[3] || 0, 10)
    };
  }
  return null;
}

// Check if iOS version supports push notifications
export function isIOSPushVersionSupported() {
  const version = getIOSVersion();
  if (!version) return true; // Not iOS, assume supported
  
  // Push notifications supported from iOS 16.4+
  return version.major > 16 || (version.major === 16 && version.minor >= 4);
}

// Enhanced iOS notification permission request
export async function requestIOSNotificationPermission() {
  if (!isIOSDevice()) {
    // Not iOS, use standard method
    return await Notification.requestPermission();
  }

  if (!isIOSInstalled()) {
    throw new Error('App must be installed to home screen for iOS notifications');
  }

  if (!isIOSPushVersionSupported()) {
    throw new Error('iOS version does not support push notifications');
  }

  try {
    // iOS-specific permission request flow
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      console.log('📱 iOS: Notification permission granted');
    }
    
    return permission;
    
  } catch (error) {
    console.error('iOS notification permission request failed:', error);
    throw error;
  }
}